import { useEffect, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { UnitCascade, type CascadeValue } from '@/features/voters/UnitCascade'
import type { SurveyStratumDto } from '@/shared/api/types'
import { createStratum, updateStratum } from './api'
import { CODE_LIST_UNAVAILABLE, useCodes } from './useVocabulary'
import { selectionLabel } from './vocab'

/**
 * What each selection does to the weights, which is the only reason to prefer
 * one. Keyed by code so it travels with whatever `stratumSelection` serves; a
 * selection with no sentence written for it is still offered, without one.
 */
const SELECTION_MEANING: Record<string, string> = {
  PROPORTIONAL: 'Target n follows the stratum\'s share of the population. Design weights come '
    + 'out near 1 and the sample is self-weighting.',
  EQUAL: 'The same target n in every stratum. Small strata get read reliably; the '
    + 'weights that put them back in proportion are large.',
  DISPROPORTIONATE: 'A target set by hand, usually to oversample somewhere. Only defensible if '
    + 'the population size is declared, because nothing else can undo the oversample.',
}

/**
 * Declaring one stratum.
 *
 * ## Why `populationSize` is the field that matters
 *
 * The design weight for a stratum is `N_h / n_h`. `N_h` is this field and there
 * is no other source for it in the product — not the geography tree, which knows
 * how many wards a constituency has and nothing about how many adults live in
 * them. A stratum saved without one still constrains fieldwork, but it
 * contributes a design weight of 1, which is the same as not being weighted.
 *
 * ## The selection is a choice, and it has no default
 *
 * `StratumSelection` is served by `GET /surveys/vocabulary` as
 * `stratumSelection`, so the picker is built from the response and `vocab.tsx`
 * supplies the English. The initial value is empty rather than `PROPORTIONAL`: a
 * default is itself a code typed into the client, and it is the one place drift
 * would be invisible — every stratum saved without opening the select would
 * carry it. The selection is the assumption behind the sample size, so it is
 * worth one click.
 */
export function StratumDialog({ open, surveyId, stratum, onClose, onSaved }: {
  open: boolean
  surveyId: string
  /** `null` is "add". An existing row is "edit" — the code stays editable, the server owns collisions. */
  stratum: SurveyStratumDto | null
  onClose: () => void
  onSaved: () => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [populationSize, setPopulationSize] = useState('')
  const [targetN, setTargetN] = useState('')
  const selections = useCodes('stratumSelection')
  const [selectionCode, setSelectionCode] = useState('')
  const [unit, setUnit] = useState<CascadeValue>({ path: [] })
  /**
   * The unit already on the record, carried separately from the cascade.
   *
   * `UnitCascade` is built from a county→station `path` and the DTO gives back
   * only `unitId` + `unitName`, so the cascade cannot be repopulated from an
   * edit. Dropping the id instead would be silent data loss: `StratumRequest`
   * has no "leave it alone" — a PUT with no `unitId` clears the unit, so an
   * edit that only changed the target n would also unhook the stratum from its
   * geography.
   */
  const [keptUnitId, setKeptUnitId] = useState<string | null>(null)
  const [keptUnitName, setKeptUnitName] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setCode(stratum?.code ?? '')
    setName(stratum?.name ?? '')
    setPopulationSize(stratum?.populationSize === null || stratum?.populationSize === undefined
      ? '' : String(stratum.populationSize))
    setTargetN(stratum ? String(stratum.targetN) : '')
    setSelectionCode(stratum?.selectionCode ?? '')
    setUnit({ path: [] })
    setKeptUnitId(stratum?.unitId ?? null)
    setKeptUnitName(stratum?.unitName ?? null)
    setError(null)
  }, [open, stratum])

  const unitId = unit.unitId ?? keptUnitId
  const selectionMeaning = SELECTION_MEANING[selectionCode]

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const body = {
        code: code.trim(),
        name: name.trim(),
        unitId: unitId ?? null,
        // '' is "not declared" and 0 is "a stratum with nobody in it". Number('')
        // is 0, which would turn the first into the second silently.
        populationSize: populationSize.trim() === '' ? null : Number(populationSize),
        targetN: targetN.trim() === '' ? 0 : Number(targetN),
        selectionCode,
      }
      if (stratum) await updateStratum(surveyId, stratum.id, body)
      else await createStratum(surveyId, body)
      onSaved()
    } catch (e) {
      // The server's own sentence — a duplicate code comes back as
      // `survey/stratum-code-taken` naming the code that is already taken.
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="md"
      title={stratum ? `Stratum ${stratum.code}` : 'New stratum'}
      description="A stratum is a slice of the population sampled on its own terms, and the unit a design weight is computed for."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          {/*
            Not gated on a client-side validity check. Code and name are @NotBlank
            and the code is unique per poll; the server says which one is wrong and
            in what words, and a button that greys itself out cannot.
          */}
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy} onClick={submit}>
            {busy ? 'Saving…' : stratum ? 'Save' : 'Add stratum'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" required
            hint="Up to 32 characters. Every assignment and every export row names it.">
            <input className="erp-input h-9 w-full font-mono text-erp-sm" value={code} maxLength={32}
              onChange={(e) => setCode(e.target.value)} placeholder="NRB-C" />
          </Field>
          <Field label="Name" required hint="What a supervisor calls this slice.">
            <input className="erp-input h-9 w-full" value={name} maxLength={200}
              onChange={(e) => setName(e.target.value)} placeholder="Nairobi Central" />
          </Field>
        </div>

        <Field label="Where"
          hint="Optional. Ties the stratum to the geography tree so assignments inside it can be scope-checked.">
          <div className="space-y-1.5">
            {keptUnitId && !unit.unitId && (
              <div className="flex items-center gap-2 text-erp-sm">
                <span className="text-muted-foreground">Currently</span>
                <span>{keptUnitName ?? keptUnitId}</span>
                <button type="button"
                  className="erp-btn erp-btn-ghost h-7 text-erp-xs text-chocolate"
                  onClick={() => { setKeptUnitId(null); setKeptUnitName(null) }}>
                  Detach
                </button>
              </div>
            )}
            <UnitCascade value={unit} onChange={setUnit} />
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Population size"
            hint="N_h — how many people this stratum contains. Leave empty and the design weight for everybody in it is 1.">
            <input className="erp-input h-9 w-full text-right tabular-nums" type="number" min={0}
              value={populationSize} onChange={(e) => setPopulationSize(e.target.value)}
              placeholder="412000" />
          </Field>
          <Field label="Target n"
            hint="n_h — how many interviews this stratum is meant to yield.">
            <input className="erp-input h-9 w-full text-right tabular-nums" type="number" min={0}
              value={targetN} onChange={(e) => setTargetN(e.target.value)} placeholder="600" />
          </Field>
        </div>

        <Field label="Selection" required
          hint={selectionMeaning
            ?? 'How this stratum\u2019s target n was arrived at \u2014 the assumption the design '
              + 'weights are computed from.'}
          error={selections.unavailable ? CODE_LIST_UNAVAILABLE : undefined}>
          <select className="erp-input h-9 w-full" value={selectionCode}
            disabled={selections.loading || selections.unavailable}
            onChange={(e) => setSelectionCode(e.target.value)}>
            <option value="">
              {selections.loading ? 'Loading\u2026' : 'Choose the selection\u2026'}
            </option>
            {/* A selection the server serves and this build has no label for is
                still offered, as its raw code. Dropping it would hide a design the
                server considers legal. */}
            {selections.codes.map((c) => (
              <option key={c} value={c}>{selectionLabel(c)}</option>
            ))}
          </select>
        </Field>

        {selections.unavailable && (
          <Note tone="bad">
            A stratum cannot be saved without a selection the server recognises, and the list of
            them could not be read.
            <button type="button" className="erp-btn-ghost h-7 text-erp-xs ml-2"
              onClick={() => selections.reload()}>
              Try again
            </button>
          </Note>
        )}

        {populationSize.trim() === '' && (
          <Note tone="info">
            Without a population size this stratum can still shape fieldwork, but it adds nothing
            to a weight run: `N_h / n_h` has no numerator, the run falls back to a weight of 1,
            and the estimates come out stamped as unweighted.
          </Note>
        )}

        {stratum && stratum.achieved > 0 && (
          <Note tone="info">
            {stratum.achieved.toLocaleString()} interview
            {stratum.achieved === 1 ? '' : 's'} already sit in this stratum. Changing its
            population size changes their weight retrospectively — the next run will produce
            different numbers from the last one, for a reason that lives here and not on Results.
          </Note>
        )}
      </div>
    </Modal>
  )
}
